<template>
  <main class="login-container credential-login">
    <!-- 新增：登录状态变化提示 -->
    <div v-if="showLoginStatusChangeTip" class="login-status-change-tip">
      <el-alert
        title="登录状态变化，请刷新"
        type="info"
        :closable="false"
        show-icon
      >
        <template #default>
          <p>检测到其他标签页已完成登录，请刷新页面以同步最新状态。</p>
          <div class="tip-actions">
            <el-button type="primary" size="small" @click="refreshPage">
              刷新页面
            </el-button>
            <el-button size="small" @click="dismissTip">
              忽略
            </el-button>
          </div>
        </template>
      </el-alert>
    </div>

    <section class="credential-brand" aria-labelledby="credential-title">
      <div class="credential-copy"><span class="brand-eyebrow">E · IDENTITY</span><h1 id="credential-title">一个身份，<br>连接 E时代。</h1><p>你的账号、设备与应用，<br class="mobile-break">在这里统一管理。</p></div>
      <CredentialScene />
    </section>
    <section class="credential-form-card" aria-label="登录 E时代通行证">
        <div class="login-form-section" v-show="currentMethod === 'account'">
          <div class="section-header">
            <h2>欢迎回来</h2><p>登录 E时代通行证</p>
          </div>
          <nav class="credential-method-tabs" aria-label="登录方式">
              <button type="button" :aria-pressed="preferredMethod === 'email'" @click="chooseLoginMethod('email')">邮箱验证码</button>
              <button type="button" :aria-pressed="preferredMethod === 'password'" @click="chooseLoginMethod('password')">密码登录</button>
              <button type="button" @click="handlePasskeyLogin" :disabled="passkeyLoginLoading">通行密钥</button>
            </nav>
            <div class="section-content" v-loading="loading">
            <div class="login-form">
              <transition name="email-slide" mode="out-in">
                <div v-if="!emailCodeSent && !showPasswordField" key="method-select" class="email-step">
                  <el-form :model="formData" @submit.prevent="startEmailCodeLogin">
                    <el-form-item :label="preferredMethod === 'email' ? '邮箱地址' : '邮箱 / 用户名'">
                      <el-input
                        v-model="formData.username"
                        :placeholder="preferredMethod === 'email' ? '请输入邮箱地址' : '请输入邮箱或用户名'"
                        clearable
                        @clear="handleClearUsername"
                      >
                        <template #prefix>
                          <el-icon><User /></el-icon>
                        </template>
                      </el-input>
                    </el-form-item>
                  </el-form>
                  <el-button type="primary" class="method-continue" @click="startEmailCodeLogin" :loading="sendingCode">继续</el-button>
                </div>
                <div v-else-if="!emailCodeSent && showPasswordField" key="password-form" class="email-step">
                  <el-form ref="accountFormRef" :model="formData" :rules="rules" @submit.prevent="handleSubmit" @keydown.enter.prevent="handleSubmit">
                    <el-form-item prop="username" :label="preferredMethod === 'email' ? '邮箱地址' : '邮箱 / 用户名'">
                      <el-input
                        v-model="formData.username"
                        :placeholder="preferredMethod === 'email' ? '请输入邮箱地址' : '请输入邮箱或用户名'"
                        clearable
                        @clear="handleClearUsername"
                      >
                        <template #prefix>
                          <el-icon><User /></el-icon>
                        </template>
                      </el-input>
                    </el-form-item>
                    <el-form-item prop="password" label="密码">
                      <el-input
                        v-model="formData.password"
                        :type="showPassword ? 'text' : 'password'"
                        placeholder="密码"
                        autofocus
                      >
                        <template #prefix>
                          <el-icon><Lock /></el-icon>
                        </template>
                        <template #suffix>
                          <el-icon class="cursor-pointer" @click="showPassword = !showPassword">
                            <Hide v-if="showPassword" />
                            <View v-else />
                          </el-icon>
                        </template>
                      </el-input>
                    </el-form-item>
                  </el-form>
                </div>
                <div v-else key="pin-code" class="email-step code-step">
                  <div class="code-sent-info">
                    <div class="code-sent-row">
                      <el-icon class="code-sent-icon"><Message /></el-icon>
                      <span class="code-sent-email">{{ emailFormData.email }}</span>
                      <el-button text type="primary" size="small" @click="resetEmailCodeStep">修改</el-button>
                    </div>
                    <span class="code-sent-hint">请输入邮箱收到的6位验证码</span>
                  </div>
                  <div class="pin-code-container">
                    <input
                      v-for="(_, index) in 6"
                      :key="index"
                      :ref="el => setPinRef(el, index)"
                      type="text"
                      inputmode="numeric"
                      maxlength="1"
                      class="pin-code-input"
                      :class="{ 'has-value': emailCodeDigits[index] }"
                      :value="emailCodeDigits[index]"
                      @input="handlePinInput(index, $event)"
                      @keydown="handlePinKeydown(index, $event)"
                      @paste="handlePinPaste($event)"
                      @focus="handlePinFocus(index)"
                    />
                  </div>
                  <div class="code-actions">
                    <span v-if="countdown > 0" class="code-countdown-text">{{ countdown }}秒后可重新获取</span>
                    <el-button v-else text type="primary" size="small" @click="triggerEmailSendCodeCaptcha" :loading="sendingCode">
                      重新获取验证码
                    </el-button>
                  </div>
                </div>
              </transition>
            </div>
            <div class="login-actions" v-if="showPasswordField || emailCodeSent">
              <el-button
                type="primary" @click="handleSubmit" :loading="loading" class="login-btn">
                登录
              </el-button>
              <div v-if="showPasswordField" class="back-link" @click="chooseLoginMethod('email')">
                ← 返回选择登录方式
              </div>
            </div>
            <div class="helper-links">
              <el-button text @click="goToRegister">没有账号？立即注册</el-button>
              <el-button text @click="showPasswordRecoveryTip">忘记密码？</el-button>
            </div>
            <div class="third-party-login">
              <div class="divider"><span>其他登录方式</span></div>
              <div class="third-party-icons"><button type="button" class="provider-button" @click="currentMethod = 'miniprogram'"><span class="wechat-mini"><svg viewBox="0 0 24 24" width="1em" height="1em"><path fill="#07C160" d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.006-.27-.012-.405-.012v-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z"/></svg></span><span>微信扫码</span></button>
                <button type="button" class="provider-button" @click="handleQQLogin"><t-icon name="logo-qq" /><span>QQ</span></button>
                <button type="button" class="provider-button" @click="handleGoogleLogin"><el-icon class="oauth-icon"><svg viewBox="0 0 48 48" width="1em" height="1em"><path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12 s5.373-12,12-12c3.059,0,5.842,1.154,7.957,3.043l5.657-5.657C33.754,6.053,29.131,4,24,4C12.955,4,4,12.955,4,24 s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/><path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,16.108,18.961,13,24,13c3.059,0,5.842,1.154,7.957,3.043l5.657-5.657 C33.754,6.053,29.131,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/><path fill="#4CAF50" d="M24,44c5.166,0,9.859-1.977,13.409-5.193l-6.19-5.238C29.211,35.091,26.715,36,24,36 c-5.202,0-9.619-3.317-11.278-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/><path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.794,2.237-2.231,4.166-3.987,5.569 c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.988,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/></svg></el-icon><span>Google</span></button>
                <button type="button" class="provider-button" @click="handleGithubLogin"><el-icon class="oauth-icon"><svg viewBox="0 0 24 24" width="1em" height="1em"><path fill="currentColor" d="M12 0C5.37 0 0 5.37 0 12a12 12 0 0 0 8.21 11.44c.6.11.82-.26.82-.58v-2.02c-3.34.73-4.04-1.61-4.04-1.61c-.55-1.39-1.35-1.76-1.35-1.76c-1.11-.76.08-.75.08-.75c1.22.09 1.86 1.25 1.86 1.25c1.09 1.86 2.86 1.32 3.56 1.01c.11-.79.43-1.32.78-1.62c-2.66-.3-5.47-1.33-5.47-5.93c0-1.31.47-2.38 1.24-3.22c-.12-.3-.54-1.52.12-3.17c0 0 1.01-.32 3.3 1.23c.96-.27 1.98-.4 3-.41c1.02.01 2.04.14 3 .41c2.29-1.55 3.3-1.23 3.3-1.23c.66 1.65.24 2.87.12 3.17c.77.84 1.24 1.91 1.24 3.22c0 4.61-2.81 5.63-5.49 5.92c.44.38.83 1.13.83 2.28v3.38c0 .32.22.69.82.58A12 12 0 0 0 24 12c0-6.63-5.37-12-12-12z"/></svg></el-icon><span>GitHub</span></button>
              </div>
            </div>
          </div>
          <!-- 隐藏的极验实例 -->
          <div style="display:none">
            <GeetestCaptcha ref="accountBindGeetestRef" product="bind" :onSuccess="onAccountBindSuccess" />
            <GeetestCaptcha ref="emailBindGeetestRef" product="bind" :onSuccess="onEmailBindSuccess" />
          </div>
        </div>

        <div class="wechat-scan-section" v-if="currentMethod === 'miniprogram'">
          <button type="button" class="return-to-login" @click="currentMethod = 'account'">← 返回登录</button><div class="section-header wechat-header">
            <el-icon class="wechat-icon"><svg viewBox="0 0 24 24" width="1em" height="1em"><path fill="#07C160" d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.006-.27-.012-.405-.012v-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z"/></svg></el-icon>
            <h2>微信扫码登录</h2>
          </div>
          <div class="section-content wechat-content" v-loading="miniprogramLoading">
            <div class="wechat-qr-wrapper">
              <img
                v-if="miniprogramQrImage"
                :src="miniprogramQrImage"
                alt="微信小程序扫码登录"
                class="wechat-qr-code"
              />
              <div v-else class="wechat-qr-placeholder" @click="generateMiniprogramCode">
                <template v-if="miniprogramStatus === 'expired'">
                  <el-icon class="qr-expired-icon"><svg viewBox="0 0 24 24" width="1em" height="1em"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg></el-icon>
                  <p class="qr-placeholder-text">小程序码已失效</p>
                  <p class="qr-placeholder-subtext">点击重新生成</p>
                </template>
                <template v-else>
                  <el-icon class="qr-placeholder-icon"><svg viewBox="0 0 24 24" width="1em" height="1em"><path fill="currentColor" d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.006-.27-.012-.405-.012v-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z"/></svg></el-icon>
                  <p class="qr-placeholder-text">点击生成小程序码</p>
                </template>
              </div>
            </div>
            <div class="wechat-info">
              <div class="wechat-countdown" v-if="miniprogramCountdown > 0">
                <span class="countdown-text">有效期：{{ formatCountdown(miniprogramCountdown) }}</span>
              </div>
              <div v-if="miniprogramCode" class="wechat-code-display">
                <span class="code-label">识别码：</span>
                <span class="code-value">{{ miniprogramCode.slice(0, 5) }}-{{ miniprogramCode.slice(5, 10) }}</span>
              </div>
              <el-button
                v-if="miniprogramQrImage && miniprogramCountdown <= 0"
                type="primary"
                text
                size="small"
                @click="generateMiniprogramCode"
                :loading="miniprogramLoading"
              >
                重新生成
              </el-button>
            </div>
            <p class="wechat-tip">微信扫一扫，确认即可登录</p>
            <!-- 状态提示 -->
            <div class="wechat-status" v-if="miniprogramStatus !== 'waiting'">
              <div v-if="miniprogramStatus === 'confirmed'" class="status-confirmed">
                <el-icon class="status-icon"><svg viewBox="0 0 24 24" width="1em" height="1em"><path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg></el-icon>
                <p>授权已确认，正在登录...</p>
              </div>
              <div v-else-if="miniprogramStatus === 'expired'" class="status-expired">
                <p>小程序码已失效</p>
                <el-button type="primary" text size="small" @click="generateMiniprogramCode">重新生成</el-button>
              </div>
              <div v-else-if="miniprogramStatus === 'register'" class="status-register">
                <p class="status-subtitle">该微信未绑定账号</p>
                <p class="register-countdown-text" v-if="registerCountdown > 0">
                  将在 <span class="countdown-number">{{ registerCountdown }}</span> 秒后自动创建
                </p>
                <div class="register-actions-compact">
                  <el-button size="small" @click="cancelRegister">取消</el-button>
                  <el-button type="primary" size="small" @click="confirmRegister" :loading="loading">立即注册</el-button>
                </div>
              </div>
            </div>
          </div>
        </div>
    </section>
  </main>
</template>
<script setup lang="ts">
/* eslint-disable no-undef */
import { ref, reactive, onMounted, onBeforeMount, watch, onUnmounted, computed, nextTick } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { useRouter } from 'vue-router';
import { useUserStore } from '@/store/user';
import { useSharedStore } from '@/store/shared';
import request from '@/utils/request';
import { User, Lock, Message, Key, View, Hide } from '@element-plus/icons-vue';
import type { FormInstance } from 'element-plus';
import { config } from '@/config'
import GeetestCaptcha from '@/components/GeetestCaptcha.vue';
import CredentialScene from '@/components/CredentialScene.vue';
import { LogoQqIcon } from 'tdesign-icons-vue-next';

const router = useRouter();
const userStore = useUserStore();
const sharedStore = useSharedStore();

// 新增：登录状态变化提示相关
const showLoginStatusChangeTip = ref(false);

// PC 端检测



// 侧边面板显示控制（动画延迟）


const currentMethod = ref('account');

const formData = reactive({
  username: sharedStore.email || '',
  password: '',
});

const emailFormData = reactive({
  email: sharedStore.email || '',
  code: '',
});

const loading = ref(false);
const sendingCode = ref(false);
const passkeyLoginLoading = ref(false);
const showPasswordField = ref(false);

// 小程序扫码登录相关
const miniprogramLoading = ref(false);
const miniprogramStatus = ref('waiting'); // waiting, confirmed, expired, register
const miniprogramScene = ref('');
const miniprogramQrImage = ref('');
const miniprogramCode = ref('');
const miniprogramSecretKey = ref(''); // 客户端绑定密钥，用于轮询时验证身份
const pollTimer = ref<ReturnType<typeof setInterval> | null>(null);
const pendingWechatData = ref<any>(null);
const isGeneratingCode = ref(false); // 防止重复生成验证码
const miniprogramCountdown = ref(0); // 小程序验证码倒计时
const miniprogramCountdownTimer = ref<ReturnType<typeof setInterval> | null>(null);
const registerCountdown = ref(0); // 新用户注册倒计时
const registerCountdownTimer = ref<ReturnType<typeof setInterval> | null>(null);

const rules = {
  username: [
    { required: true, message: '请输入用户名或邮箱', trigger: 'blur' },
    {
      validator: (rule: any, value: string, callback: any) => {
        if (value.includes('@')) {
          // 如果是邮箱格式，使用邮箱验证
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            callback(new Error('请输入正确的邮箱格式'));
          }
        } else if (value.length < 3) {
          callback(new Error('用户名建议不少于3个字符'));
        }
        callback();
      },
      trigger: 'blur'
    }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    { min: 6, max: 20, message: '密码长度为6-20位', trigger: 'blur' },
  ],
};

const emailRules = {
  email: [
    { required: true, message: '请输入邮箱', trigger: 'blur' },
    { type: 'email', message: '请输入正确的邮箱格式', trigger: 'blur' },
  ],
  code: [
    { required: true, message: '请输入验证码', trigger: 'blur' },
    { len: 6, message: '验证码长度为6位', trigger: 'blur' },
  ],
};

const countdown = ref(0);
const startCountdown = () => {
  countdown.value = 60;
  const timer = setInterval(() => {
    countdown.value--;
    if (countdown.value <= 0) {
      clearInterval(timer);
    }
  }, 1000);
};



// 旧弹窗方式已移除，改为 bind 触发



// 已不使用的第三方登录占位方法移除

const handleGoogleLogin = () => {
  const state = Math.random().toString(36).slice(2)
  localStorage.setItem('google_login_state', state)

  // 保存当前的 redirect 参数，以便登录后能回到授权页面
  const redirect = router.currentRoute.value.query.redirect as string
  if (redirect) {
    localStorage.setItem('oauth_login_redirect', redirect)
  }

  const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${encodeURIComponent(config.google.clientId)}&redirect_uri=${encodeURIComponent(config.google.redirectUri)}&response_type=code&scope=${encodeURIComponent(config.google.scope)}&state=${encodeURIComponent(state)}`
  window.location.href = url
}

const handleGithubLogin = () => {
  const state = Math.random().toString(36).slice(2)
  localStorage.setItem('github_login_state', state)

  // 保存当前的 redirect 参数，以便登录后能回到授权页面
  const redirect = router.currentRoute.value.query.redirect as string
  if (redirect) {
    localStorage.setItem('oauth_login_redirect', redirect)
  }

  const url = `https://github.com/login/oauth/authorize?client_id=${encodeURIComponent(config.github.clientId)}&redirect_uri=${encodeURIComponent(config.github.redirectUri)}&scope=${encodeURIComponent(config.github.scope)}&state=${encodeURIComponent(state)}`
  window.location.href = url
}

const goToRegister = () => {
  // 保留当前输入的邮箱信息
  if (formData.username && formData.username.includes('@')) {
    sharedStore.setEmail(formData.username);
  }
  router.push('/register');
};

const accountFormRef = ref<FormInstance>();
const emailFormRef = ref<FormInstance>();
// bind 模式隐藏实例引用
const accountBindGeetestRef = ref<InstanceType<typeof GeetestCaptcha>>();
const emailBindGeetestRef = ref<InstanceType<typeof GeetestCaptcha>>();
const emailCodeInputRef = ref<any>();

const emailCodeSent = ref(false);
const emailCodeDigits = ref<string[]>(['', '', '', '', '', '']);
const pinRefs = ref<(HTMLInputElement | null)[]>(new Array(6).fill(null));

const setPinRef = (el: any, index: number) => {
  if (el) pinRefs.value[index] = el as HTMLInputElement;
};

const handlePinInput = (index: number, event: Event) => {
  const input = event.target as HTMLInputElement;
  const value = input.value.replace(/\D/g, '');
  emailCodeDigits.value[index] = value.slice(-1);
  emailFormData.code = emailCodeDigits.value.join('');

  if (value && index < 5) {
    nextTick(() => pinRefs.value[index + 1]?.focus());
  }

  if (emailCodeDigits.value.every(d => d) && emailFormData.code.length === 6) {
    nextTick(() => pinRefs.value[5]?.blur());
    setTimeout(() => handleSubmit(), 300);
  }
};

const handlePinKeydown = (index: number, event: KeyboardEvent) => {
  if (event.key === 'Backspace') {
    if (!emailCodeDigits.value[index] && index > 0) {
      emailCodeDigits.value[index - 1] = '';
      emailFormData.code = emailCodeDigits.value.join('');
      nextTick(() => pinRefs.value[index - 1]?.focus());
    }
  } else if (event.key === 'ArrowLeft' && index > 0) {
    pinRefs.value[index - 1]?.focus();
  } else if (event.key === 'ArrowRight' && index < 5) {
    pinRefs.value[index + 1]?.focus();
  } else if (event.key === 'Enter') {
    handleSubmit();
  }
};

const handlePinPaste = (event: ClipboardEvent) => {
  event.preventDefault();
  const paste = event.clipboardData?.getData('text')?.replace(/\D/g, '').slice(0, 6);
  if (!paste) return;

  for (let i = 0; i < 6; i++) {
    emailCodeDigits.value[i] = paste[i] || '';
  }
  emailFormData.code = emailCodeDigits.value.join('');

  nextTick(() => {
    const nextEmpty = emailCodeDigits.value.findIndex(d => !d);
    if (nextEmpty >= 0) {
      pinRefs.value[nextEmpty]?.focus();
    } else {
      pinRefs.value[5]?.focus();
    }
  });

  if (paste.length === 6) {
    setTimeout(() => handleSubmit(), 300);
  }
};

const handlePinFocus = (index: number) => {
  pinRefs.value[index]?.select();
};

const resetEmailCodeStep = () => {
  emailCodeSent.value = false;
  emailCodeDigits.value = ['', '', '', '', '', ''];
  emailFormData.code = '';
  showPasswordField.value = false;
};

const isEmailFormat = computed(() => {
  const val = formData.username;
  return val && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
});

const startEmailCodeLogin = () => {
  if (!formData.username) {
    ElMessage.warning('请先输入邮箱地址');
    return;
  }
  if (!isEmailFormat.value) {
    ElMessage.warning('当前输入不是邮箱，如需邮箱验证码登录，请输入邮箱地址');
    return;
  }
  emailFormData.email = formData.username;
  sharedStore.setEmail(formData.username);
  triggerEmailSendCodeCaptcha();
};

// 修复首次进入页面时验证码按钮可能未渲染的问题：在动画完成/容器显示后刷新一次
const refreshCaptchas = () => {
  // bind 模式无需刷新占位
};

// 删除占位相关状态

// 区分邮箱 bind 验证用途：sendCode | login
const emailBindPurpose = ref<'sendCode' | 'login'>('sendCode');

// 触发邮箱验证码发送的 bind 模式验证码
const triggerEmailSendCodeCaptcha = async () => {
  try {
    if (!emailCodeSent.value) {
      if (emailFormRef.value) {
        await emailFormRef.value.validateField('email');
      } else if (!emailFormData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailFormData.email)) {
        ElMessage.error('请输入正确的邮箱格式');
        return;
      }
    }
    emailBindPurpose.value = 'sendCode';
    emailBindGeetestRef.value?.show?.();
  } catch (error: any) {
    ElMessage.error(error.message || '验证失败');
  }
};

// bind 成功回调：账号密码登录
const onAccountBindSuccess = async (payload: any) => {
  try {
    loading.value = true;
    await userStore.login({
      username: formData.username,
      password: formData.password,
      lotNumber: payload.lotNumber,
      captchaOutput: payload.captchaOutput,
      passToken: payload.passToken,
      genTime: payload.genTime
    });
    formData.password = '';
    ElMessage.success('登录成功');
    const redirect = (router.currentRoute.value.query.redirect as string) || '/profile';
    router.push(redirect.startsWith('/login') ? '/profile' : redirect);
  } catch (error: any) {
    ElMessage.error(error.message || '登录失败');
  } finally {
    loading.value = false;
  }
};

// bind 成功回调：邮箱验证码发送 / 邮箱登录
const onEmailBindSuccess = async (payload: any) => {
  try {
    if (emailBindPurpose.value === 'sendCode') {
      sendingCode.value = true;
      await userStore.sendEmailCodeWithGeetest({
        email: emailFormData.email,
        lotNumber: payload.lotNumber,
        captchaOutput: payload.captchaOutput,
        passToken: payload.passToken,
        genTime: payload.genTime
      });
      ElMessage.success('验证码已发送');
      startCountdown();
      emailCodeSent.value = true;
      emailCodeDigits.value = ['', '', '', '', '', ''];
      emailFormData.code = '';
      setTimeout(() => { pinRefs.value[0]?.focus(); }, 500);
    } else {
      loading.value = true;
      const loginData = {
        email: emailFormData.email,
        code: emailFormData.code,
        lotNumber: payload.lotNumber,
        captchaOutput: payload.captchaOutput,
        passToken: payload.passToken,
        genTime: payload.genTime
      };
      await userStore.loginWithEmail(loginData);
      emailFormData.code = '';
      ElMessage.success('登录成功');
      const redirect = (router.currentRoute.value.query.redirect as string) || '/profile';
      router.push(redirect.startsWith('/login') ? '/profile' : redirect);
    }
  } catch (error: any) {
    ElMessage.error(error.message || (emailBindPurpose.value === 'sendCode' ? '发送验证码失败' : '登录失败'));
  } finally {
    if (emailBindPurpose.value === 'sendCode') {
      sendingCode.value = false;
    } else {
      loading.value = false;
    }
    emailBindPurpose.value = 'sendCode';
  }
};

// 通行密钥登录（发现模式，不需要用户名）
const handlePasskeyLogin = async () => {
  try {
    passkeyLoginLoading.value = true

    // 1. 开始登录，获取挑战（发现模式，不需要用户名）
    const options = await userStore.startPasskeyLogin()

    // 2. 转换选项格式为 WebAuthn API 需要的格式
    // 将 Base64URL 转换为 Base64，然后解码（需要添加填充）
    const base64UrlToBase64 = (base64Url: string): string => {
      let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      // 添加填充
      while (base64.length % 4) {
        base64 += '='
      }
      return base64
    }

    const publicKeyCredentialRequestOptions: any = {
      challenge: Uint8Array.from(atob(base64UrlToBase64(options.challenge)), c => c.charCodeAt(0)),
      timeout: options.timeout,
      userVerification: options.userVerification || 'preferred'
    }

    // 发现模式：不传 allowCredentials，让浏览器自动发现所有可用的通行密钥
    if (options.allowCredentials && options.allowCredentials.length > 0) {
      publicKeyCredentialRequestOptions.allowCredentials = options.allowCredentials.map((cred: any) => ({
        id: Uint8Array.from(atob(base64UrlToBase64(cred.id)), c => c.charCodeAt(0)),
        type: cred.type
      }))
    }

    // 3. 调用 WebAuthn API 获取断言
    const assertion = await navigator.credentials.get({
      publicKey: publicKeyCredentialRequestOptions
    }) as any

    if (!assertion) {
      throw new Error('通行密钥验证失败')
    }

    // 4. 转换响应为 Base64URL
    const response = assertion.response as any

    // 将 ArrayBuffer 转换为 Base64URL
    const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
      const bytes = new Uint8Array(buffer)
      let binary = ''
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i])
      }
      return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
    }

    const assertionBase64 = {
      id: assertion.id, // 已经是 Base64URL
      response: {
        clientDataJSON: arrayBufferToBase64(response.clientDataJSON),
        authenticatorData: arrayBufferToBase64(response.authenticatorData),
        signature: arrayBufferToBase64(response.signature)
      }
    }

    // 5. 完成登录
    await userStore.finishPasskeyLogin(
      options.challenge,
      JSON.stringify(assertionBase64)
    )

    ElMessage.success('登录成功')
    formData.password = ''
    const redirect = (router.currentRoute.value.query.redirect as string) || '/profile'
    router.push(redirect.startsWith('/login') ? '/profile' : redirect)
  } catch (error: any) {
    console.error('通行密钥登录失败:', error)
    if (error.name === 'NotAllowedError') {
      ElMessage.error('操作被取消或设备不支持')
    } else if (error.name === 'InvalidStateError') {
      ElMessage.error('通行密钥验证失败，请重试')
    } else {
      // 使用通用错误信息，不显示具体错误原因
      ElMessage.error('无法使用通行密钥登录，可能是设备不支持')
    }
  } finally {
    passkeyLoginLoading.value = false
  }
}

// 生成小程序码（无需极验）
const generateMiniprogramCode = async () => {
    if (isGeneratingCode.value) return;
  try {
    isGeneratingCode.value = true;
    miniprogramLoading.value = true;
    miniprogramStatus.value = 'waiting';
    const response = await request.post('/auth/miniprogram-login/generate');
    const responseData = response as any;
    if (responseData.code === 200 && responseData.data) {
      miniprogramScene.value = responseData.data.scene;
      miniprogramQrImage.value = responseData.data.qrCodeImage || '';
      miniprogramCode.value = responseData.data.verificationCode || responseData.data.scene || '';
      miniprogramSecretKey.value = responseData.data.secretKey || ''; // 保存客户端绑定密钥
      startPolling();
      startMiniprogramCountdown();
    } else {
      ElMessage.error(responseData.message || '生成小程序码失败');
    }
  } catch (error: any) {
    ElMessage.error('生成小程序码失败');
  } finally {
    miniprogramLoading.value = false;
    isGeneratingCode.value = false;
  }
};


const handleSubmit = async () => {
  try {
    if (loading.value) return;

    if (emailCodeSent.value) {
      if (emailFormData.code.length !== 6) {
        ElMessage.warning('请输入完整的6位验证码');
        return;
      }
      emailBindPurpose.value = 'login';
      emailBindGeetestRef.value?.show?.();
    } else if (showPasswordField.value) {
      if (!accountFormRef.value) return;
      await accountFormRef.value.validate();
      accountBindGeetestRef.value?.show?.();
    }
  } catch (error: any) {
    ElMessage.error({
      message: error.message || '登录失败',
      grouping: true
    });
  }
};

const emit = defineEmits(['animation-complete']);
const preferredMethod = ref<'email' | 'password'>('email');
const chooseLoginMethod = (method: 'email' | 'password') => {
  preferredMethod.value = method;
  emailCodeSent.value = false;
  showPasswordField.value = method === 'password';
  formData.password = '';
};
onMounted(() => { emit('animation-complete'); });

const showPassword = ref(false);

// 新增：刷新页面方法
const refreshPage = () => {
  window.location.reload();
};

// 新增：忽略提示方法
const dismissTip = () => {
  showLoginStatusChangeTip.value = false;
  sharedStore.clearLoginStatusChanged();
};

// 新增：检查登录状态变化
const checkLoginStatusChange = () => {
  if (sharedStore.loginStatusChanged) {
    showLoginStatusChangeTip.value = true;
  }
};

// 新增：初始化登录状态监听
const initLoginStatusListener = () => {
  // 初始化共享状态监听器
  sharedStore.initLoginStatusListener();

  // 监听自定义登录状态变化事件
  window.addEventListener('loginStatusChanged', () => {
    // 延迟检查，确保状态已更新
    setTimeout(() => {
      checkLoginStatusChange();
    }, 100);
  });

  // 页面可见性变化时检查状态
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      // 页面变为可见时，检查登录状态
      setTimeout(() => {
        checkLoginStatusChange();
      }, 100);
    }
  });
};

onBeforeMount(async () => {
  // 检查是否有错误参数（从授权页面跳转过来）
  const route = router.currentRoute.value
  if (route.query.error === 'token_expired') {
    ElMessage.error(route.query.error_description as string || '登录已过期，请重新登录')
  }

  if (userStore.token) {
    try {
      await userStore.checkAuth()
      const redirect = (route.query.redirect as string) || '/profile'
      router.replace(redirect)
    } catch (error) {
      // token 无效，清除登录状态
      userStore.logout()
    }
  }

  // 新增：初始化登录状态监听
  initLoginStatusListener();

  // 新增：检查登录状态变化
  setTimeout(() => {
    checkLoginStatusChange();
  }, 100);
})

const showPasswordRecoveryTip = () => {
  ElMessageBox.alert(
    '请使用邮箱验证码登录后修改密码，或重新注册新账号，如有特别情况，请联系 accountrecovery@qifalab.com',
    '找回密码提示',
    {
      confirmButtonText: '知道了',
      type: 'info'
    }
  ).catch(() => {
    // 用户点击关闭按钮时的处理
    // 这里可以为空，但需要catch来防止报错
  })
}

const handleQQLogin = () => {
  // 生成随机state用于防止CSRF攻击
  const state = Math.random().toString(36).substring(7);
  localStorage.setItem('qq_login_state', state);

  // 保存当前的 redirect 参数，以便登录后能回到授权页面
  const redirect = router.currentRoute.value.query.redirect as string;
  if (redirect) {
    localStorage.setItem('oauth_login_redirect', redirect);
  }

  // 直接跳转到 QQ 授权页面（不使用 SDK，避免冲突）
  const params = new URLSearchParams({
    response_type: 'token',
    client_id: config.qq.appId,
    redirect_uri: config.qq.redirectUri,
    scope: 'get_user_info',
    state: state
  });

  window.location.href = `https://graph.qq.com/oauth2.0/authorize?${params.toString()}`;
};

// 监听username变化，如果是邮箱格式则同步到email字段和共享存储
watch(() => formData.username, (newValue) => {
  if (newValue && newValue.includes('@')) {
    emailFormData.email = newValue;
    sharedStore.setEmail(newValue);
  }
});

// 监听email变化，同步到共享存储
watch(() => emailFormData.email, (newValue) => {
  if (newValue) {
    sharedStore.setEmail(newValue);
  }
});

// 监听登录方式变化：进入小程序登录时自动获取一次小程序码，离开时停止计时/轮询
watch(() => currentMethod.value, (newMethod, oldMethod) => {
  if (newMethod === 'miniprogram') {
    miniprogramStatus.value = 'waiting';
    // 仅在当前没有有效小程序码时自动生成，避免重复刷新
    if (!miniprogramQrImage.value && !miniprogramLoading.value) {
      generateMiniprogramCode();
    }
  } else if (oldMethod === 'miniprogram') {
    stopMiniprogramCountdown();
    stopPolling();
    miniprogramCode.value = '';
    miniprogramQrImage.value = '';
    miniprogramScene.value = '';
    miniprogramStatus.value = 'waiting';
  }
});

const handleClearUsername = () => {
  // 无论用户名是什么格式，都清除共享存储
  sharedStore.clearEmail();
  // 同步清除邮箱登录方式的邮箱
  emailFormData.email = '';
};

const handleClearEmail = () => {
  // 清除共享存储
  sharedStore.clearEmail();
  // 同步清除账号密码登录方式的邮箱
  if (formData.username.includes('@')) {
    formData.username = '';
  }
};

// 小程序扫码登录相关方法：
// 页面加载时不主动生成，由切换到“小程序登录”Tab 时自动生成

const formatCountdown = (seconds: number) => {
  if (seconds <= 0) return '';
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  if (min > 0) return `${min}:${sec.toString().padStart(2, '0')}`;
  return `${sec}秒`;
};

// 开始小程序验证码倒计时
const startMiniprogramCountdown = () => {
  miniprogramCountdown.value = 300; // 5分钟倒计时
  miniprogramCountdownTimer.value = setInterval(() => {
    miniprogramCountdown.value--;
    if (miniprogramCountdown.value <= 0) {
      if (miniprogramCountdownTimer.value) {
        clearInterval(miniprogramCountdownTimer.value);
        miniprogramCountdownTimer.value = null;
      }
      miniprogramQrImage.value = '';
      miniprogramCode.value = '';
      miniprogramScene.value = '';
      miniprogramStatus.value = 'expired';
      if (pollTimer.value) {
        clearInterval(pollTimer.value);
        pollTimer.value = null;
      }
    }
  }, 1000);
};

// 停止小程序验证码倒计时
const stopMiniprogramCountdown = () => {
  if (miniprogramCountdownTimer.value) {
    clearInterval(miniprogramCountdownTimer.value);
    miniprogramCountdownTimer.value = null;
  }
  miniprogramCountdown.value = 0;
};

// 开始新用户注册倒计时
const startRegisterCountdown = () => {
  stopRegisterCountdown(); // 先清除可能存在的倒计时
  registerCountdown.value = 10; // 10秒倒计时
  registerCountdownTimer.value = setInterval(() => {
    registerCountdown.value--;
    if (registerCountdown.value <= 0) {
      stopRegisterCountdown();
      // 倒计时结束，自动注册
      confirmRegister();
    }
  }, 1000);
};

// 停止新用户注册倒计时
const stopRegisterCountdown = () => {
  if (registerCountdownTimer.value) {
    clearInterval(registerCountdownTimer.value);
    registerCountdownTimer.value = null;
  }
  registerCountdown.value = 0;
};

// bind 模式已直接触发，不需要占位回调

// 小程序码生成逻辑已内聚到 generateMiniprogramCode 中，无需额外占位回调

const startPolling = () => {
  if (pollTimer.value) {
    clearInterval(pollTimer.value);
  }

  pollTimer.value = setInterval(async () => {
    try {
      const response = await request.get(`/auth/miniprogram-login/status/${miniprogramScene.value}?flow=login&secretKey=${miniprogramSecretKey.value}`);

      const responseData = response as any;
      if (responseData.code === 200) {
        const { status, token, user, message } = responseData.data;

        if (status === 'confirmed' && token && user) {
          // 登录成功
          miniprogramStatus.value = 'confirmed';
          stopPolling();
          userStore.token = token;
          userStore.userInfo = user;
          localStorage.setItem('token', token);

          // 新增：通知其他标签页登录状态变化
          userStore.notifyLoginStatusChange();

          ElMessage.success('登录成功');
          const redirect = (router.currentRoute.value.query.redirect as string) || '/profile';
          router.push(redirect.startsWith('/login') ? '/profile' : redirect);
        } else if (status === 'expired') {
          miniprogramStatus.value = 'expired';
          miniprogramQrImage.value = ''; // 清空二维码，激活重新获取逻辑
          stopPolling();
        } else if (status === 'register_required') {
          // 需要注册新账号
          miniprogramStatus.value = 'register';
          pendingWechatData.value = { code: miniprogramScene.value };
          stopPolling();
          // 启动10秒倒计时，倒计时结束自动注册
          startRegisterCountdown();
        } else if (status === 'forbidden') {
          stopPolling();
          ElMessage.error(message || '该用户不可用，请联系管理员');
        }
      }
    } catch (error) {
      console.error('轮询登录状态失败:', error);
    }
  }, 5000); // 每5秒轮询一次，减轻服务器压力并避免重复提示
};

const stopPolling = () => {
  if (pollTimer.value) {
    clearInterval(pollTimer.value);
    pollTimer.value = null;
  }
  // 停止倒计时
  stopMiniprogramCountdown();
};

const cancelRegister = () => {
  stopRegisterCountdown(); // 停止注册倒计时
  miniprogramStatus.value = 'waiting';
  pendingWechatData.value = null;
  stopMiniprogramCountdown(); // 停止小程序码倒计时
  generateMiniprogramCode(); // 重新生成验证码
};

const confirmRegister = async () => {
  try {
    if (!pendingWechatData.value) {
      ElMessage.error('微信数据缺失');
      return;
    }

    // 停止倒计时（无论是手动点击还是自动触发）
    stopRegisterCountdown();
    loading.value = true;

    // 调用注册接口
    const response = await request.post('/auth/register/wechat', {
      wechat_data: pendingWechatData.value
    });

    const responseData = response as any;
    if (responseData.code === 200) {
      // 注册成功，自动登录
      userStore.token = responseData.data.token;
      userStore.userInfo = responseData.data.user;
      localStorage.setItem('token', responseData.data.token);

      // 新增：通知其他标签页登录状态变化
      userStore.notifyLoginStatusChange();

      ElMessage.success('注册并登录成功');

      const redirect = (router.currentRoute.value.query.redirect as string) || '/profile';
      router.push(redirect.startsWith('/login') ? '/profile' : redirect);
    } else {
      ElMessage.error(responseData.message || '注册失败');
    }
  } catch (error: any) {
    console.error('注册失败:', error);
    ElMessage.error('注册失败');
  } finally {
    loading.value = false;
  }
};

// 页面卸载时清理轮询
onUnmounted(() => {
  stopPolling();
  stopMiniprogramCountdown();
  stopRegisterCountdown(); // 清理注册倒计时
  isGeneratingCode.value = false;

  // 清理窗口大小监听器


  // 新增：清理事件监听器
  // 注意：由于使用了匿名函数，这里无法直接移除，但不会造成内存泄漏
});
</script>

<style scoped>
.credential-login { display: grid; grid-template-columns: minmax(0,1.15fr) minmax(0,1fr); gap: 68px; align-items: center; max-width: 1200px; min-height: calc(100svh - 165px); box-sizing: border-box; margin: auto; padding: 42px 48px; }
.credential-brand { position: relative; min-width: 0; }
.credential-copy { position: relative; z-index: 1; padding-left: 20px; }
.brand-eyebrow { font-size: 11px; letter-spacing: .24em; color: #617b94; font-weight: 600; }
.credential-copy h1 { font-size: clamp(38px,4vw,56px); line-height: 1.2; letter-spacing: -.035em; font-weight: 650; margin: 18px 0; color: #172c43; }
.credential-copy p { color: #617489; font-size: 15px; line-height: 1.9; margin: 0; }
.mobile-break { display: none; }
.credential-brand :deep(.credential-scene) { height: 410px; margin: -6px 0 -14px; }
.credential-form-card { background: #ffffffed; border: 1px solid #e0e7ee; box-shadow: 0 22px 65px #24476e0c,0 2px 8px #24476e04; padding: 38px; border-radius: 24px; min-width: 0; }
.section-header h2 { margin: 0 0 10px; color: #172c43; font-size: 28px; font-weight: 600; letter-spacing: -.02em; }
.section-header p { margin: 0; color: #708295; font-size: 14px; }
.credential-method-tabs { display: flex; justify-content: space-between; gap: 8px; border-bottom: 1px solid #e5ebf0; margin: 30px 0 26px; }
.credential-method-tabs button { background: transparent; border: 0; border-bottom: 2px solid transparent; font: inherit; font-size: 13px; color: #6a7c8e; padding: 0 0 13px; cursor: pointer; }
.credential-method-tabs button[aria-pressed='true'] { color: #246dd0; border-color: #246dd0; font-weight: 600; }
button:focus-visible, .pin-code-input:focus-visible { outline: 3px solid #8ab9f2; outline-offset: 4px; }
button:disabled { cursor: wait; opacity: .6; }
:deep(.el-form-item) { display: block; margin-bottom: 20px; }
:deep(.el-form-item__label) { display: block; float: none; height: auto; font-size: 13px; color: #53677a; padding: 0 0 9px; line-height: 1.4; }
:deep(.el-input__wrapper) { min-height: 48px; box-sizing: border-box; border-radius: 10px; background: #fcfdfe; padding: 1px 14px; }
:deep(.el-input__inner) { font-size: 14px; }
:deep(.el-input__prefix) { margin-right: 4px; color: #95a4b4; }
.method-continue, .login-btn { width: 100%; height: 48px; border-radius: 10px; font-size: 15px; font-weight: 500; }
.login-actions { margin-top: 18px; }
.helper-links { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-top: 16px; }
.helper-links :deep(.el-button) { padding: 8px 0; height: auto; margin: 0; font-size: 12px; color: #667b91; }
.back-link { text-align: center; margin-top: 15px; color: #64788d; cursor: pointer; font-size: 12px; }
.third-party-login { margin-top: 28px; }
.divider { display: flex; align-items: center; gap: 15px; margin-bottom: 20px; color: #8a99a7; font-size: 11px; }
.divider::before,.divider::after { content: ''; height: 1px; background: #e9edf2; flex: 1; }
.third-party-icons { display: grid; grid-template-columns: repeat(4,minmax(0,1fr)); gap: 8px; }
.provider-button { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 9px; min-height: 68px; border: 1px solid #e6ecf2; border-radius: 10px; background: #fff; color: #53677a; font-family: inherit; font-size: 11px; cursor: pointer; transition: background .2s,border-color .2s; }
.provider-button:hover { border-color: #bbd2ea; background: #f3f8ff; }
.provider-button :deep(svg),.provider-button .el-icon { width: 21px; height: 21px; font-size: 21px; }
.wechat-mini { display: flex; color: #07a854; }
.cursor-pointer { cursor: pointer; }
.code-sent-info { margin-bottom: 20px; }
.code-sent-row { display: flex; align-items: center; gap: 6px; min-width: 0; }
.code-sent-email { flex: 1; overflow: hidden; text-overflow: ellipsis; font-size: 13px; }
.code-sent-hint { font-size: 12px; color: #7c8d9c; }
.pin-code-container { display: flex; gap: 8px; }
.pin-code-input { width: 0; flex: 1; min-width: 0; height: 50px; text-align: center; font-size: 23px; color: #172c43; border: 1px solid #ccd8e3; border-radius: 8px; background: #fff; }
.pin-code-input.has-value { border-color: #6d9fd9; }
.resend-code { text-align: center; margin-top: 14px; font-size: 12px; color: #7c8d9c; }
.email-slide-enter-active,.email-slide-leave-active { transition: opacity .12s; }
.email-slide-enter-from,.email-slide-leave-to { opacity: 0; }
.wechat-scan-section { min-height: 420px; display: flex; flex-direction: column; align-items: center; }
.return-to-login { align-self: flex-start; padding: 0; margin-bottom: 32px; border: 0; background: none; color: #64788d; cursor: pointer; font: inherit; font-size: 13px; }
.wechat-header { display: flex; align-items: center; gap: 10px; margin-bottom: 22px; }
.wechat-header h2 { font-size: 23px; margin: 0; }
.wechat-icon { font-size: 25px; }
.wechat-content { width: 100%; text-align: center; }
.wechat-qr-wrapper { width: 200px; height: 200px; padding: 12px; box-sizing: border-box; border: 1px solid #e2eaf1; border-radius: 16px; background: white; margin: 0 auto 18px; }
.wechat-qr-code { width: 100%; height: 100%; object-fit: contain; }
.wechat-qr-placeholder { height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; cursor: pointer; color: #5b7a6c; }
.qr-placeholder-icon { font-size: 44px; color: #07a854; }.qr-placeholder-text { font-size: 13px; }.qr-placeholder-subtext { font-size: 12px; }
.wechat-info,.wechat-tip,.wechat-status { font-size: 12px; color: #7a8b99; line-height: 1.8; }
.wechat-code-display { margin: 8px 0; }.code-value { color: #3c5d79; font-family: monospace; }
.register-actions-compact { display: flex; justify-content: center; margin-top: 12px; }
.login-status-change-tip { position: fixed; top: 82px; right: 20px; width: min(360px,calc(100vw - 40px)); z-index: 200; }
.tip-actions { margin-top: 10px; }
@media (max-width: 1000px) { .credential-login { gap: 36px; padding: 36px 28px; }.credential-form-card { padding: 28px; }.credential-copy { padding-left: 0; } }
@media (max-width: 760px) {
 .credential-login { display: block; padding: 28px 20px 32px; min-height: auto; max-width: 510px; }
 .credential-brand { display: flex; align-items: center; margin: 0 0 24px; }
 .credential-copy { flex: 1; }.credential-copy h1 { font-size: 29px; margin: 10px 0; }.brand-eyebrow { font-size: 9px; letter-spacing: .14em; }.credential-copy p { font-size: 11px; line-height: 1.8; }.mobile-break { display: inline; }
 .credential-brand :deep(.credential-scene) { width: 140px; height: 190px; margin: 0 -10px 0 0; flex-shrink: 0; }
 .credential-form-card { padding: 28px 24px; border-radius: 20px; }.section-header h2 { font-size: 25px; }.credential-method-tabs { margin-top: 25px; }.third-party-icons { gap: 6px; }
}
@media (prefers-reduced-motion: reduce) { *,*::before,*::after { transition: none !important; animation: none !important; } }
</style>
